import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const JournalMsichanaWaAI = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Gallery images with IDs for navigation
  const galleryImages = [
    { id: 1, url: '/images/journal/MsichanawaAI1.jpg', caption: '', description: '' },
    { id: 2, url: '/images/journal/MsichanawaAI2.jpg', caption: '', description: '' },
    { id: 3, url: '/images/journal/MsichanawaAI3.jpg', caption: '', description: ' ' },
    { id: 4, url: '/images/journal/MsichanawaAI4.jpg', caption: '', description: '' },
    { id: 5, url: '/images/journal/MsichanawaAI5.png', caption: '', description: '' },
    { id: 6, url: '/images/journal/MsichanawaAI6.jpg', caption: '', description: '' },
    { id: 7, url: '/images/journal/MsichanawaAI7.jpg', caption: 'Forum Group Discussion: Msichana wa AI.', description: '' },
    { id: 8, url: '/images/journal/MsichanawaAI8.jpg', caption: 'Forum Group Discussion: Msichana wa AI.', description: '' },
    { id: 9, url: '/images/journal/MsichanawaAI9.jpg', caption: 'Forum Group Discussion: Msichana wa AI.', description: '' },
    { id: 10, url: '/images/journal/MsichanawaAI10.jpg', caption: 'Forum Group Discussion: Msichana wa AI.', description: '' },
    { id: 11, url: '/images/journal/MsichanawaAI11.jpg', caption: 'Forum Group Discussion: Msichana wa AI.', description: '' },
    { id: 12, url: '/images/journal/MsichanawaAI12.jpg', caption: 'Forum Group Discussion: Msichana wa AI.', description: '' },
    { id: 13, url: '/images/journal/MsichanawaAI13.jpg', caption: 'Forum Group Discussion: Msichana wa AI.', description: '' }
  ];

  const galleryRef = useRef(null);

  const scrollToGallery = () => {
    if (galleryRef.current) {
      galleryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;

      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        closeLightbox();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage]);

  // Touch/swipe navigation
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const previousIndex = currentIndex > 0 ? currentIndex - 1 : galleryImages.length - 1;
    setSelectedImage(galleryImages[previousIndex]);
  };

  const goToNext = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = currentIndex < galleryImages.length - 1 ? currentIndex + 1 : 0;
    setSelectedImage(galleryImages[nextIndex]);
  };

  // Survey data
  const surveyData = {
    total: 94,
    gender: { female: 49, male: 40, other: 5 },
    age: "25–44 years old (74% of participants)",
    device: "Smartphone (Primary) and Personal Laptop (Secondary)"
  };

  const agreementData = [
    { metric: "Productivity: AI tools save me time in my daily life", agree: 30, neutral: 5, disagree: 7 },
    { metric: "Professionalism: AI helps with work/creative projects", agree: 31, neutral: 6, disagree: 5 },
    { metric: "Localization: AI understands the Kenyan context well", agree: 18, neutral: 16, disagree: 8 },
    { metric: "Accessibility: I have easy access to data and devices", agree: 36, neutral: 4, disagree: 2 },
    { metric: "Future Outlook: Curious about what is possible with AI", agree: 36, neutral: 4, disagree: 2 },
    { metric: "Language Need: AI needs Kenyan languages to be useful", agree: 31, neutral: 6, disagree: 5 },
    { metric: "Confidence: Feel confident using AI even in English", agree: 33, neutral: 5, disagree: 4 },
    { metric: "Gender Gap: Men benefit more from AI than women", agree: 6, neutral: 9, disagree: 27 },
    { metric: "Online Safety: Women face more AI-amplified harassment", agree: 13, neutral: 15, disagree: 14 },
  ];

  return (
    <>
      <section className="journal-entry-header">
        <div className="container">
          <div className="entry-meta-top">
            <span className="research-tag">African Women in AI</span>
          </div>
          <h1>Msichana wa AI: Culture, AI and Storytelling</h1>
          <p className="entry-abstract">Exploring how artificial intelligence is shaping culture, creativity, and everyday life in Kenya, with a focus on gender, technology, and storytelling.</p>
        </div>
      </section>

      <section className="journal-entry-content wide-container">
        <div className="container">
          {/* Page 1 – Research Journal Overview */}
          <div className="entry-section highlight-box">
            <h2>Research Overview</h2>
            <p>Msichana wa AI is a research journal exploring how artificial intelligence is shaping culture, creativity, and everyday life in Kenya. The project examines the intersection of technology, cultural identity, and storytelling, with a particular interest in how emerging AI systems interact with local knowledge, language, and social realities.</p>
            <p>The research is part of a broader inquiry into how Kenyan communities interpret, adapt, and negotiate artificial intelligence within their own cultural contexts.</p>
          </div>

          {/* Research Focus */}
          <div className="entry-section">
            <h2>Research Focus</h2>
            <p>This phase of the Msichana wa AI project explored whether men and women use AI differently in Kenya, particularly in creative and everyday contexts. We investigated how gender shapes interaction with AI, emotional use, privacy concerns, and perceptions of AI's role in creativity and wellbeing.</p>
          </div>

          {/* Research Components */}
          <div className="entry-section">
            <h2>Research Components</h2>
            <div className="two-col-grid">
              <div className="col">
                <h3>1. Survey: AI & Everyday Life in Kenya</h3>
                <p>Explored first impressions of AI, trust, privacy, creative usage, and gender dynamics. Targeted urban and peri-urban creatives, knowledge workers, and technology users. (N=42)</p>
              </div>
              <div className="col">
                <h3>2. Interview Series: Msichana wa AI</h3>
                <p>Narrative interviews exploring personal reflections from individuals interacting with AI within Kenyan social and professional environments. (N=52)</p>
              </div>
            </div>
            <div className="two-col-grid">
              <div className="col">
                <h3>3. Focus Group Discussion</h3>
                <p>A narrative-focused discussion with six participants (three women, three men) from diverse creative backgrounds, including writing, cinematography, performance, art curation, and creative entrepreneurship.</p>
              </div>
              <div className="col">
                <h3>Total Participants</h3>
                <p className="stat-number-large">94</p>
                <p>Combined survey and interview participants</p>
              </div>
            </div>
          </div>

          {/* Page 2 – Theoretical Framework */}
          <div className="entry-section">
            <h2>Theoretical Framework</h2>
            <p>This research is grounded in an interdisciplinary framework drawing from technology studies, cultural analysis, and gender studies. Rather than treating artificial intelligence purely as a technical innovation, the research situates AI within social, cultural, and relational systems.</p>
          </div>

          <div className="framework-grid">
            <div className="framework-card">
              <h3>1. Technology as Cultural Infrastructure</h3>
              <p>Technologies are embedded within language, economic realities, social trust, and cultural norms. In Kenya, AI adoption is shaped by these cultural contexts rather than by technological access alone. AI derives meaning through Kenyan languages, social systems, and community norms.</p>
            </div>

            <div className="framework-card">
              <h3>2. Human–AI Co-Creation</h3>
              <p>Many Kenyan users perceive AI not as a replacement for human intelligence but as a collaborator that assists with repetitive tasks while humans retain creativity, interpretation, and ethical judgment. AI functions primarily as an assistant or collaborator, supporting creativity and decision-making without replacing human judgment.</p>
            </div>

            <div className="framework-card">
              <h3>3. Gender, Digital Power, and Algorithmic Bias</h3>
              <p>Feminist technology perspectives help examine how AI intersects with gendered experiences of digital space, including harassment, access barriers, and representation within datasets. AI can amplify existing inequalities. Research examines how women and men experience these dynamics differently and explores potential mitigation strategies.</p>
            </div>
          </div>

          {/* Page 3 – Methodology Overview */}
          <div className="entry-section">
            <h2>Methodology Overview</h2>
            <p>The study used a mixed qualitative and exploratory research approach combining surveys and narrative interviews. The aim was to capture both broad patterns of AI usage and individual lived experiences of interacting with artificial intelligence.</p>
            <p><strong>Analytical Approach:</strong> The project used thematic analysis to synthesize survey and focus group data, highlighting patterns of AI adoption, trust, creative collaboration, emotional reliance, and digital access.</p>
          </div>

          {/* Page 4 – Survey: AI & Everyday Life in Kenya */}
          <div className="entry-section">
            <h2>Survey: AI & Everyday Life in Kenya</h2>
            <p>The survey explored public perceptions of AI across everyday contexts including:</p>
            <ul className="project-list">
              <li>First impressions of AI</li>
              <li>Common use cases</li>
              <li>Trust and privacy concerns</li>
              <li>AI in work and creative practice</li>
              <li>Emotional and relational interactions with AI</li>
              <li>Gender dynamics in digital environments</li>
            </ul>
            <p>Participants included creatives, knowledge workers, and urban technology users. The survey was exploratory and designed to identify emerging patterns rather than produce statistically representative national data.</p>
          </div>

          {/* Page 5 – Interview Series */}
          <div className="entry-section">
            <h2>Interview Series: Msichana wa AI</h2>
            <p>The narrative interview series titled "Msichana wa AI" explores personal reflections from individuals interacting with artificial intelligence within Kenyan social and professional environments.</p>
            <p>The interviews explore:</p>
            <ul className="project-list">
              <li>Emotional interpretations of AI</li>
              <li>How AI shapes communication and relationships</li>
              <li>Experimentation with AI tools in creative practice</li>
              <li>Gendered experiences of emerging technologies</li>
            </ul>
            <p>Narrative interviews allow participants to express complex cultural and emotional responses that structured surveys may not capture.</p>
          </div>

          {/* Focus Group Discussion - Detailed */}
          <div className="entry-section highlight-box">
            <h2>Focus Group Discussion Findings</h2>
            <p>A narrative-focused discussion with six participants (three women, three men) from diverse creative backgrounds, including writing, cinematography, performance, art curation, and creative entrepreneurship.</p>
            <p>Participants engaged with unique questions around digital violence, AI in creativity, emotional and self-therapy uses, utopian vs. dystopian possibilities, and AI policy in the creative sector.</p>
          </div>

          {/* GALLERY NAVIGATION BUTTON */}
          <div className="gallery-navigation">
            <button onClick={scrollToGallery} className="gallery-nav-btn">
              <i className="fas fa-images"></i>
              <span>Explore the Gallery <i className="fas fa-arrow-down"></i></span>
            </button>
          </div>

          <div className="entry-section">
            <h3>Key Observations</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Observation</th>
                  <th>Finding</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Practical Use</strong></td>
                  <td>5 out of 6 participants saw AI as practically useful, particularly for creative work and understanding complex systems such as agriculture.</td>
                </tr>
                <tr>
                  <td><strong>Emotional Support</strong></td>
                  <td>Emotional or relational use of AI was generally not supported, though it was acknowledged as a potential tool for venting. Two participants were open to AI as a form of emotional support.</td>
                </tr>
                <tr>
                  <td><strong>Gendered Risks</strong></td>
                  <td>AI can exacerbate digital violence and GBV, particularly against women. All participants were in agreement.</td>
                </tr>
                <tr>
                  <td><strong>Accessibility</strong></td>
                  <td>Accessibility and cost remain barriers, with one participant opting out entirely due to privacy and digital footprint concerns.</td>
                </tr>
                <tr>
                  <td><strong>Disability Support</strong></td>
                  <td>AI may hold potential for addressing disability-related challenges through functional assistive tools. All participants concurred.</td>
                </tr>
                <tr>
                  <td><strong>Intimacy with AI</strong></td>
                  <td>One participant was open to AI as an alternative intimate partner due to increasing technology use and rising gender-based violence. The others disagreed, emphasizing that AI cannot replace human intimacy.</td>
                </tr>
                <tr>
                  <td><strong>Disability Insights</strong></td>
                  <td>Participants living with disabilities noted AI's potential for practical assistive tools, enhancing accessibility and creative participation.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="entry-section">
            <h3>Gendered Use of AI</h3>
            <div className="two-col-grid">
              <div className="col">
                <h4>Women</h4>
                <p>Women were generally more emotional users of AI, using it to explore creative ideas or for personal reflection.</p>
              </div>
              <div className="col">
                <h4>Men</h4>
                <p>Men engaged differently, focusing more on practical tasks.</p>
              </div>
            </div>
            <p className="note"><strong>Privacy and Digital Footprints:</strong> Most participants were cautious about giving personal information to AI, citing fear of misuse or loss of control. One participant noted that online platforms already collect so much personal data, so sharing with AI felt no different.</p>
          </div>

          <div className="entry-section">
            <h3>Shame and Fear in AI Use</h3>
            <p>About half of the participants reported feelings of shame or fear while using AI. Concerns included:</p>
            <ul className="project-list">
              <li>Feeling that AI could "suspend your brain" or replace creative thought.</li>
              <li>Pressure from "purist" audiences who judge AI-assisted work as less authentic.</li>
              <li>Fear of losing personal agency or control over creative work.</li>
            </ul>
          </div>

          <div className="entry-section">
            <h3>Creativity and AI</h3>
            <p>Participants strongly agreed that AI cannot replace human creativity or the emotional depth humans bring to art. Most felt that AI is a tool, not a replacement.</p>
          </div>

          <div className="entry-section">
            <h3>Environmental and Ethical Concerns</h3>
            <p>Some participants raised concerns about AI's environmental impact, including water use and energy consumption, as well as potential corporate overuse displacing human artists.</p>
          </div>

          <div className="entry-section">
            <h3>Policy Recommendations</h3>
            <p>Participants suggested policies to ensure AI supports rather than replaces human creativity, including:</p>
            <ul className="project-list">
              <li>Prioritizing human artists in creative industries.</li>
              <li>Setting ethical guidelines for AI in art.</li>
              <li>Encouraging AI use as a supplement rather than a replacement.</li>
            </ul>
          </div>

          {/* DATA REPORT */}
          <div className="entry-section">
            <h2>DATA REPORT: AI & EVERYDAY LIFE IN KENYA</h2>
            <p className="subtitle">Prepared for: ZIRAYA Institute Research Review</p>
            <p className="subtitle">Subject: Impact of Artificial Intelligence on Gender, Culture, and Productivity in Kenya</p>
            <p className="subtitle">Data Sources: AI & Everyday Life Survey (N=42) and Msichana wa AI Interview Series (N=52)</p>
          </div>

          {/* Executive Summary */}
          <div className="entry-section">
            <h3>1. EXECUTIVE SUMMARY & DEMOGRAPHICS</h3>
            <p>This report synthesizes data from <strong>94 participants</strong> to understand how Kenyans—particularly women—are navigating the rise of AI. The findings indicate high adoption for productivity but significant gaps in cultural relevance and gender-based safety.</p>

            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Total Respondents</div>
                <div className="stat-number">{surveyData.total}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Gender Split</div>
                <div className="stat-number">52% Female ({surveyData.gender.female})</div>
                <div className="stat-number">43% Male ({surveyData.gender.male})</div>
                <div className="stat-number">5% Other ({surveyData.gender.other})</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Primary Age Demographic</div>
                <div className="stat-number">{surveyData.age}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Primary Device</div>
                <div className="stat-number">{surveyData.device}</div>
              </div>
            </div>
          </div>

          {/* Quantitative Analysis */}
          <div className="entry-section">
            <h3>2. QUANTITATIVE ANALYSIS (SURVEY DATA)</h3>
            <p>Agreement Levels on the Impact of AI on Daily Life (Scale: Agree = scores 1-2; Neutral = 3; Disagree = scores 4-5)</p>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Metric / Question</th>
                  <th>Agree</th>
                  <th>Neutral</th>
                  <th>Disagree</th>
                </tr>
              </thead>
              <tbody>
                {agreementData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.metric}</td>
                    <td>{item.agree}</td>
                    <td>{item.neutral}</td>
                    <td>{item.disagree}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Qualitative Themes */}
          <div className="entry-section">
            <h3>3. QUALITATIVE THEMES (INTERVIEW INSIGHTS)</h3>

            <div className="theme-item">
              <h4>A. Cultural Resonance & "The Kenyan Voice"</h4>
              <ul className="project-list">
                <li>High demand for Swahili, Sheng, and Mother Tongue integration (Kikuyu, etc.)</li>
                <li>Users want AI to understand local geography and "Kenyan banter"</li>
                <li>The Forgiveness Factor: Some participants are more forgiving of Kenyan-voiced AI mistakes, others less so because it "should know the area"</li>
              </ul>
            </div>

            <div className="theme-item">
              <h4>B. Professional Impact & The "Partner" Model</h4>
              <ul className="project-list">
                <li>Usage: Scriptwriting, research, coding, academic help</li>
                <li>Replacement Anxiety: 16 respondents fear replacement, majority see AI as a "Partner" handling grunt work</li>
              </ul>
            </div>

            <div className="theme-item">
              <h4>C. Emotional and Mental Health Support</h4>
              <ul className="project-list">
                <li>AI as a Confidant: 19 respondents describe their relationship as "Experimental"; some use AI as a non-judgmental venting tool</li>
                <li>Human Nuance: AI lacks human touch and shared lived experience necessary for deep emotional healing</li>
              </ul>
            </div>
          </div>

          {/* Key Concerns */}
          <div className="entry-section">
            <h3>4. KEY CONCERNS & BARRIERS</h3>
            <ul className="project-list">
              <li><strong>Data Privacy:</strong> 21 respondents anxious about AI knowing too much</li>
              <li><strong>Digital Divide:</strong> 21 respondents face cost, language, or device limits</li>
              <li><strong>Originality:</strong> 20 respondents feel AI makes them "more creative but less original" suggesting a crisis of identity for Kenyan creators</li>
            </ul>
          </div>

          {/* Action Items */}
          <div className="entry-section">
            <h3>5. PROPOSED ACTION ITEMS FOR DONORS</h3>
            <ul className="project-list">
              <li><strong>Investment in Local LLMs:</strong> Fund local mother tongue languages, Swahili and Sheng language models to increase inclusivity</li>
              <li><strong>Gender-Centric Digital Safety:</strong> Address AI-amplified harassment and algorithmic gender bias</li>
              <li><strong>Digital Literacy for Rural Areas:</strong> Bridge the gap for participants excluded due to data/device limitations</li>
              <li><strong>AI for Governance:</strong> Explore AI tools to trace fraud and increase public sector transparency</li>
            </ul>
          </div>

          {/* Research Outputs */}
          <div className="entry-section">
            <h2>Research Outputs</h2>
            <ul className="project-list">
              <li>Working Papers</li>
              <li>Focus Group Discussion Documentation (including images and transcribed insights)</li>
              <li>Public Conversations (planned)</li>
            </ul>
            <p className="note"><strong>Note:</strong> Unlike earlier plans, a toolkit was not developed for this phase. The primary output was research documentation and a detailed focus group discussion that provided rich qualitative insights into AI's cultural and emotional relevance in Kenya.</p>
          </div>

          {/* Page 7 – Future Research Agenda */}
          <div className="entry-section">
            <h2>Future Research Agenda</h2>

            <div className="future-grid">
              <div className="future-card">
                <h3>1. Indigenous Language AI Systems</h3>
                <p>Future research will explore AI models that understand Swahili, Sheng, and Kenyan ethnic languages.</p>
              </div>

              <div className="future-card">
                <h3>2. AI and Creative Economies</h3>
                <p>Research will examine how AI influences artistic authorship, creative workflows, and digital storytelling in Kenya's creative industries.</p>
              </div>

              <div className="future-card">
                <h3>3. AI and Digital Ethics in African Contexts</h3>
                <p>The project seeks to contribute to African-centered frameworks for AI ethics addressing data ownership, algorithmic bias, and cultural representation.</p>
              </div>

              <div className="future-card">
                <h3>4. Gender and Algorithmic Inequality</h3>
                <p>Future work will examine how AI interacts with gender dynamics and whether AI systems can be designed to reduce online harassment and bias.</p>
              </div>
            </div>
          </div>

          {/* Gallery Section */}
          {galleryImages.length > 0 && (
            <div className="journal-gallery" ref={galleryRef}>
              <h2>Research Gallery</h2>
              <p className="gallery-subtitle"></p>
              <div className="gallery-grid">
                {galleryImages.map((image) => (
                  <div
                    key={image.id}
                    className="gallery-item"
                    onClick={() => openLightbox(image)}
                  >
                    <div className="gallery-image-wrapper">
                      <img
                        src={image.url}
                        alt={image.caption}
                        className="gallery-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/placeholder.jpg';
                        }}
                      />
                    </div>
                    {image.caption && (
                      <div className="gallery-caption">
                        <h4>{image.caption}</h4>
                        {image.description && <p>{image.description}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Call for Collaboration */}
          <div className="entry-section collaboration-box">
            <h2>Call for Collaboration</h2>
            <p>The Msichana wa AI project is entering its next phase, exploring deeper intersections of AI, creativity, mental health, and gender in Kenya. I invite researchers, creatives, and practitioners interested in contributing to this ongoing inquiry to join the project or reach out to collaborate. Your insights and participation will help shape a richer understanding of how AI can be integrated ethically, creatively, and culturally within Kenyan society.</p>
            <div className="contact-info">
              <p><strong>Contact:</strong> <a href="mailto:projects@zirayainstitute.org">projects@zirayainstitute.org</a></p>
            </div>
          </div>

          {/* Back to Journal Link */}
          <div className="back-to-journal">
            <Link to="/research-journal" className="text-link">← Back to Research Journal</Link>
          </div>
        </div>
      </section>

      {/* Enhanced Lightbox Modal with Navigation */}
      {selectedImage && (
        <div
          className="lightbox-modal"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <i className="fas fa-times"></i>
            </button>

            {/* Previous Button */}
            <button className="lightbox-nav prev" onClick={goToPrevious}>
              <i className="fas fa-chevron-left"></i>
            </button>

            {/* Next Button */}
            <button className="lightbox-nav next" onClick={goToNext}>
              <i className="fas fa-chevron-right"></i>
            </button>

            <div className="lightbox-image-wrapper">
              <img
                src={selectedImage.url}
                alt={selectedImage.caption}
                className="lightbox-image"
              />
            </div>

            {/* Image counter */}
            <div className="lightbox-counter">
              {galleryImages.findIndex(img => img.id === selectedImage.id) + 1} / {galleryImages.length}
            </div>

            {(selectedImage.caption || selectedImage.description) && (
              <div className="lightbox-caption">
                <h3>{selectedImage.caption}</h3>
                <p>{selectedImage.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default JournalMsichanaWaAI;